/**
 * The ONE parse entry point for the platform's stored recurrence text.
 *
 * The vault `rrule` column contractually holds either a bare RRULE VALUE
 * (`FREQ=WEEKLY;BYDAY=MO`) or a full newline-joined iCalendar component
 * fragment (`DTSTART:…\nRRULE:…\nEXDATE:…`) — the multi-line form is pinned
 * end-to-end by the expansion tests and arrives via iCal import and CalDAV
 * sync. Several hand-rolled readers split the whole value on `;` and never
 * saw the multi-line form at all, which made their guards silently inert for
 * exactly the series they mattered for. Everything in this package reads
 * recurrence text through here.
 *
 * Contract:
 *  - RFC 5545 line UNFOLDING is applied (CRLF/LF followed by SPACE or TAB
 *    continues the previous line).
 *  - `dtstart` captures the raw value plus TZID / VALUE=DATE parameters.
 *  - `rrule` is the bare RRULE VALUE (no `RRULE:` prefix), verbatim.
 *    A duplicate RRULE line is REFUSED (null) — two rules on one component
 *    have no defined meaning here.
 *  - `exdates` flattens every EXDATE line's comma-list into raw value
 *    tokens; the verbatim lines are also kept for byte-stable round-trips.
 *  - RDATE / EXRULE are DETECTED (flags) and carried verbatim in
 *    `otherLines` — policy for them lives with the callers: expansion
 *    honors them via RRuleSet; reconciliation refuses rules carrying them;
 *    user-facing ingestion rejects new EXRULE.
 *  - Size caps: an over-limit value returns null rather than being
 *    partially parsed.
 */

export interface RecurrenceTextLimits {
  /** Maximum total bytes (UTF-8). Default 8192. */
  maxBytes?: number;
  /** Maximum unfolded line count. Default 50. */
  maxLines?: number;
}

export interface ParsedDtstart {
  /** The raw property value, e.g. `20260907T090000Z` or `20260907`. */
  raw: string;
  /** TZID parameter, when present. */
  tzid: string | null;
  /** True for `VALUE=DATE` (all-day) starts. */
  isDate: boolean;
  /** The whole line, verbatim, for round-trips. */
  line: string;
}

export interface ParsedRecurrenceText {
  dtstart: ParsedDtstart | null;
  /** Bare RRULE VALUE, `RRULE:` prefix stripped, otherwise verbatim. */
  rrule: string;
  /** Raw EXDATE value tokens, comma-lists flattened, in document order. */
  exdates: string[];
  /** The verbatim EXDATE lines, for byte-stable round-trips. */
  exdateLines: string[];
  hasRdate: boolean;
  hasExrule: boolean;
  /** Every line that is not DTSTART/RRULE/EXDATE, verbatim, in order. */
  otherLines: string[];
  /** True when the input was the multi-line component form. */
  isMultiLine: boolean;
  /** Lines preceding the RRULE line (verbatim) — for order-stable rebuilds. */
  linesBeforeRrule: string[];
  /** Lines following the RRULE line (verbatim). */
  linesAfterRrule: string[];
}

const DEFAULT_MAX_BYTES = 8192;
const DEFAULT_MAX_LINES = 50;

/** RFC 5545 §3.1 unfolding: a CRLF (or bare LF) immediately followed by a
 *  single SPACE or HTAB continues the previous line. */
function unfold(value: string): string {
  return value.replace(/\r?\n[ \t]/g, '');
}

function propertyName(line: string): string {
  const sep = line.search(/[:;]/);
  return (sep === -1 ? line : line.slice(0, sep)).trim().toUpperCase();
}

/** The value after the FIRST `:` — parameters live between the name and it. */
function propertyValue(line: string): string {
  const colon = line.indexOf(':');
  return colon === -1 ? '' : line.slice(colon + 1).trim();
}

function parseDtstartLine(line: string): ParsedDtstart {
  const colon = line.indexOf(':');
  const paramsPart = colon === -1 ? line : line.slice(0, colon);
  const tzidMatch = /;TZID=([^;:]+)/i.exec(paramsPart);
  const isDate = /;VALUE=DATE(?:;|$)/i.test(paramsPart);
  return {
    raw: propertyValue(line),
    tzid: tzidMatch ? tzidMatch[1].trim() : null,
    isDate,
    line,
  };
}

/**
 * Parses the stored recurrence text. Returns null when the value is empty,
 * over the size caps, carries no RRULE, or carries more than one RRULE.
 */
export function parseRecurrenceText(
  value: string | null | undefined,
  limits: RecurrenceTextLimits = {},
): ParsedRecurrenceText | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const maxBytes = limits.maxBytes ?? DEFAULT_MAX_BYTES;
  // TextEncoder, not Buffer: this package is aliased into the BROWSER bundle
  // from source, where Buffer does not exist (vitest runs under Node, so a
  // Buffer here passes every test and still breaks every real browser).
  if (new TextEncoder().encode(trimmed).length > maxBytes) return null;

  const unfolded = unfold(trimmed);
  const lines = unfolded
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const maxLines = limits.maxLines ?? DEFAULT_MAX_LINES;
  if (lines.length === 0 || lines.length > maxLines) return null;

  // Single-line bare form (`FREQ=…` or `RRULE:FREQ=…`).
  if (lines.length === 1 && !/^(DTSTART|EXDATE|RDATE|EXRULE)[:;]/i.test(lines[0])) {
    const bare = lines[0].replace(/^RRULE:/i, '');
    if (!bare) return null;
    return {
      dtstart: null,
      rrule: bare,
      exdates: [],
      exdateLines: [],
      hasRdate: false,
      hasExrule: false,
      otherLines: [],
      isMultiLine: false,
      linesBeforeRrule: [],
      linesAfterRrule: [],
    };
  }

  let dtstart: ParsedDtstart | null = null;
  let rrule: string | null = null;
  let rruleIndex = -1;
  const exdates: string[] = [];
  const exdateLines: string[] = [];
  const otherLines: string[] = [];
  let hasRdate = false;
  let hasExrule = false;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const name = propertyName(line);
    switch (name) {
      case 'DTSTART':
        dtstart = parseDtstartLine(line);
        break;
      case 'RRULE': {
        if (rrule !== null) return null; // duplicate RRULE — refuse
        rrule = propertyValue(line) || line.replace(/^RRULE:/i, '');
        rruleIndex = i;
        break;
      }
      case 'EXDATE': {
        exdateLines.push(line);
        for (const token of propertyValue(line).split(',')) {
          const t = token.trim();
          if (t) exdates.push(t);
        }
        break;
      }
      case 'RDATE':
        hasRdate = true;
        otherLines.push(line);
        break;
      case 'EXRULE':
        hasExrule = true;
        otherLines.push(line);
        break;
      default: {
        // A stray bare `FREQ=…` line inside a multi-line value counts as the
        // RRULE when no RRULE: line names it (defensive; seen in imports).
        if (rrule === null && /^FREQ=/i.test(line)) {
          rrule = line;
          rruleIndex = i;
        } else {
          otherLines.push(line);
        }
      }
    }
  }

  if (rrule === null || !rrule.trim()) return null;

  return {
    dtstart,
    rrule: rrule.trim(),
    exdates,
    exdateLines,
    hasRdate,
    hasExrule,
    otherLines,
    isMultiLine: true,
    linesBeforeRrule: lines.slice(0, Math.max(rruleIndex, 0)),
    linesAfterRrule: rruleIndex === -1 ? [] : lines.slice(rruleIndex + 1),
  };
}

/**
 * Splits a bare RRULE VALUE into `[KEY, VALUE]` pairs, uppercasing keys.
 * Returns null when any segment is not `KEY=VALUE` — don't guess.
 */
export function rruleValueParts(rrule: string): Array<[string, string]> | null {
  const out: Array<[string, string]> = [];
  for (const segment of rrule.split(';')) {
    const trimmed = segment.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) return null;
    out.push([trimmed.slice(0, eq).toUpperCase(), trimmed.slice(eq + 1)]);
  }
  return out.length > 0 ? out : null;
}
